import React from "react";
import classNames from "classnames";
import styles from "./AppShell.module.scss";

export interface AppShellProps {
    navigation?: React.ReactNode;
    header: React.ReactNode;
    banner?: React.ReactNode;
    subNavigation?: React.ReactNode;
    showSubNavigation?: boolean;
    aside?: React.ReactNode;
    fullPage?: boolean;
    contained?: boolean;
    containerClassName?: string;
    children?: React.ReactNode;
}

/**
 * @purpose Application shell layout — a sidebar column and a content column,
 * plus the sub-navigation row, contained and full-page modes, and slot placement.
 * Slot content is unstyled; sticky header styling is owned app-side.
 * The sidebar column is sized by its content, so the navigation owns its own
 * width, open state and motion.
 *
 * The sub-navigation row carries `data-sub-navigation`, which app-tier portal
 * providers query as their portal target.
 *
 * @example
 * <AppShell
 *     navigation={<WorkspaceSideNav />}
 *     header={<AppHeader />}
 *     aside={<ChatContainer />}
 * >
 *     <PageContent />
 * </AppShell>
 */
export const AppShell: React.FC<AppShellProps> = ({
    children,
    navigation,
    header,
    banner,
    subNavigation,
    showSubNavigation = false,
    aside,
    containerClassName,
    fullPage,
    contained = false,
}) => (
    <div
        className={classNames(styles.appShell, {
            [styles.appShellContained]: contained,
        })}
    >
        {navigation ? <div className={styles.sidebar}>{navigation}</div> : null}
        <div className={styles.appShellContent}>
            {header}

            {banner}

            <div
                data-sub-navigation
                className={classNames({
                    [styles.subNavigation]: showSubNavigation,
                })}
            >
                {subNavigation}
            </div>

            <main
                className={classNames(
                    styles.container,
                    styles.mainContainer,
                    containerClassName,
                    {
                        [styles.fullPage]: fullPage,
                    },
                )}
            >
                {children}
            </main>
            {aside}
        </div>
    </div>
);
